import { Fragment, useEffect, useRef } from 'react';

import { useDeviceType } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '@/shared/constants/routes/paths';
import { TranslationKey, useInterfaceLanguage } from '@/shared/i18n';
import { PricingEntryPoint } from '@/shared/lib/pricingAttribution';
import {
  clearRecordingCheckoutReturn,
  RecordingCheckoutTarget,
  rememberRecordingCheckoutReturn,
} from '@/shared/lib/recordingCheckoutReturn';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaywallCard, PaywallDialog } from '@/shared/ui/PaywallCard';

import downloadPreviewImage from '../../assets/download-preview.webp';
import { useRecordingMinutesPaywallStore } from '../../model/useRecordingMinutesPaywallStore';
import { RecordingPaywallVariant, useRecordingPaywallStore } from '../../model/useRecordingPaywallStore';
import {
  SAutoZoomBadge,
  SAutoZoomBadgeIcon,
  SBrowserBar,
  SBrowserBody,
  SBrowserDot,
  SBrowserLine,
  SBrowserMock,
  SCaptionChip,
  SClickTarget,
  SCursor,
  SDownloadImage,
  SDownloadImagePill,
  SDownloadImagePillIcon,
  SDownloadPreview,
  SLanguageChip,
  SLanguageChips,
  SLanguageCycle,
  SLanguageCycleItem,
  SLanguageMore,
  SLanguagePill,
  SLocalizationBody,
  SLocalizationPreview,
  SLocalizationRow,
  SLocalizationWindow,
  SPreview,
  SRecBadge,
  SRecDot,
  SRipple,
  SZoomLayer,
} from './styled';

type BadgeTone = 'brand' | 'warning';

type VariantContent = {
  title: TranslationKey;
  badge: TranslationKey;
  badgeTone: BadgeTone;
  bullets: TranslationKey[];
  description: TranslationKey;
  strip: TranslationKey;
  stripTallLine: boolean;
};

const VARIANT_CONTENT: Record<RecordingPaywallVariant, VariantContent> = {
  autoZoom: {
    title: 'recordingModals.paywall.autoZoom.title',
    badge: 'recordingModals.paywall.autoZoom.badge',
    badgeTone: 'brand',
    bullets: [
      'recordingModals.paywall.autoZoom.bullet1',
      'recordingModals.paywall.autoZoom.bullet2',
      'recordingModals.paywall.autoZoom.bullet3',
    ],
    description: 'recordingModals.paywall.autoZoom.description',
    strip: 'recordingModals.paywall.autoZoom.strip',
    stripTallLine: false,
  },
  download: {
    title: 'recordingModals.paywall.download.title',
    badge: 'recordingModals.paywall.download.badge',
    badgeTone: 'brand',
    bullets: [
      'recordingModals.paywall.download.bullet1',
      'recordingModals.paywall.download.bullet2',
      'recordingModals.paywall.download.bullet3',
    ],
    description: 'recordingModals.paywall.download.description',
    strip: 'recordingModals.paywall.download.strip',
    stripTallLine: false,
  },
  recordingMinutes: {
    title: 'recordingModals.paywall.recordingMinutes.title',
    badge: 'recordingModals.paywall.recordingMinutes.badge',
    badgeTone: 'warning',
    bullets: [
      'recordingModals.paywall.recordingMinutes.bullet1',
      'recordingModals.paywall.recordingMinutes.bullet2',
      'recordingModals.paywall.recordingMinutes.bullet3',
    ],
    description: 'recordingModals.paywall.recordingMinutes.description',
    strip: 'recordingModals.paywall.recordingMinutes.strip',
    stripTallLine: false,
  },
  translateMinutes: {
    title: 'recordingModals.paywall.translateMinutes.title',
    badge: 'recordingModals.paywall.translateMinutes.badge',
    badgeTone: 'brand',
    bullets: [
      'recordingModals.paywall.translateMinutes.bullet1',
      'recordingModals.paywall.translateMinutes.bullet2',
      'recordingModals.paywall.translateMinutes.bullet3',
    ],
    description: 'recordingModals.paywall.translateMinutes.description',
    strip: 'recordingModals.paywall.translateMinutes.strip',
    stripTallLine: true,
  },
};

const CYCLING_LANGUAGE_CODES = ['EN', 'ES', 'FR', 'RU'];
const LANGUAGE_CHIP_CODES = ['EN', 'ES', 'FR', 'DE', 'PT'];
const MORE_LANGUAGES_LABEL = '+135';
const PREVIEW_RECORDING_TIME = '00:42';

const renderLines = (text: string) =>
  text.split('\n').map((line, index) => (
    <Fragment key={line}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));

const DownloadPreview = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SDownloadPreview data-testid="recording-paywall-modal-download-preview">
      <SDownloadImage
        alt={t('recordingModals.paywall.download.preview.alt')}
        draggable={false}
        src={downloadPreviewImage}
      />
      <SDownloadImagePill>
        <SDownloadImagePillIcon fill="none" viewBox="0 0 17 17">
          <path
            d="M8.5 1.3v10.4M2.9 6.2l5.6 5.6 5.6-5.6M1.3 15.5h14.4"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.4"
          />
        </SDownloadImagePillIcon>
        <span>{t('recordingModals.paywall.download.preview.button')}</span>
      </SDownloadImagePill>
    </SDownloadPreview>
  );
};

const TranslateMinutesPreview = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SLocalizationPreview data-testid="recording-paywall-modal-translate-minutes-preview">
      <SLocalizationWindow>
        <SBrowserBar>
          <SBrowserDot />
          <SBrowserDot />
          <SBrowserDot />
        </SBrowserBar>
        <SLocalizationBody>
          <SBrowserLine $tone={200} $width="58%" />
          <SLocalizationRow>
            <SCaptionChip>{t('recordingModals.paywall.translateMinutes.preview.caption')}</SCaptionChip>
            <SLanguagePill>
              <SLanguageCycle>
                {CYCLING_LANGUAGE_CODES.map((code, index) => (
                  <SLanguageCycleItem key={code} $index={index} data-anim="">
                    {code}
                  </SLanguageCycleItem>
                ))}
              </SLanguageCycle>
            </SLanguagePill>
          </SLocalizationRow>
        </SLocalizationBody>
      </SLocalizationWindow>

      <SLanguageChips>
        {LANGUAGE_CHIP_CODES.map((code, index) => (
          <SLanguageChip key={code} $active={index === 0}>
            {code}
          </SLanguageChip>
        ))}
        <SLanguageMore>{MORE_LANGUAGES_LABEL}</SLanguageMore>
      </SLanguageChips>
    </SLocalizationPreview>
  );
};

type AutoZoomPreviewProps = {
  isRecording?: boolean;
};

const AutoZoomPreview = ({ isRecording = false }: AutoZoomPreviewProps) => {
  const { t } = useInterfaceLanguage();

  return (
    <SPreview
      data-testid={
        isRecording ? 'recording-paywall-modal-recording-minutes-preview' : 'recording-paywall-modal-auto-zoom-preview'
      }
    >
      <SBrowserMock>
        <SZoomLayer data-anim="">
          <SBrowserBar>
            <SBrowserDot />
            <SBrowserDot />
            <SBrowserDot />
          </SBrowserBar>
          <SBrowserBody>
            <SBrowserLine $tone={200} $width="52%" />
            <SBrowserLine $tone={100} $width="78%" />
            <SBrowserLine $tone={100} $width="64%" />
          </SBrowserBody>
          <SClickTarget>{t('recordingModals.paywall.autoZoom.preview.cta')}</SClickTarget>
        </SZoomLayer>

        <SRipple data-anim="" />

        <SCursor data-anim="" fill="none" height={20} viewBox="0 0 24 24" width={20}>
          <path d="M5 3l14 8-6 1.5L11 19 5 3z" strokeLinejoin="round" strokeWidth="1.2" />
        </SCursor>
      </SBrowserMock>

      {isRecording && (
        <SRecBadge>
          <SRecDot data-anim="" />
          {t('recordingModals.paywall.recordingMinutes.preview.rec', { time: PREVIEW_RECORDING_TIME })}
        </SRecBadge>
      )}

      <SAutoZoomBadge>
        <SAutoZoomBadgeIcon fill="none" height={12} viewBox="0 0 24 24" width={12}>
          <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2.2" />
          <path d="M20 20l-3.6-3.6M11 8.5v5M8.5 11h5" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
        </SAutoZoomBadgeIcon>
        {t('recordingModals.paywall.autoZoom.preview.badge')}
      </SAutoZoomBadge>
    </SPreview>
  );
};

type RecordingPaywallModalContentProps = {
  variant: RecordingPaywallVariant;
  onChoosePlan: () => void;
  onClose: () => void;
};

const RecordingPaywallModalContent = ({ variant, onChoosePlan, onClose }: RecordingPaywallModalContentProps) => {
  const { isMobile } = useDeviceType();
  const { t } = useInterfaceLanguage();
  const content = VARIANT_CONTENT[variant];

  return (
    <PaywallCard
      title={renderLines(t(content.title))}
      preview={
        <>
          {variant === 'download' && <DownloadPreview />}
          {variant === 'autoZoom' && <AutoZoomPreview />}
          {variant === 'recordingMinutes' && <AutoZoomPreview isRecording />}
          {variant === 'translateMinutes' && <TranslateMinutesPreview />}
        </>
      }
      badge={t(content.badge)}
      badgeTone={content.badgeTone}
      bullets={content.bullets.map((bullet) => ({ id: bullet, text: t(bullet) }))}
      closeLabel={t('recordingModals.paywall.closeAria')}
      ctaLabel={t('recordingModals.paywall.choosePlan')}
      description={t(content.description)}
      layout={isMobile ? 'sheet' : 'dialog'}
      strip={t(content.strip)}
      stripTallLine={content.stripTallLine}
      testId="recording-paywall-modal"
      onClose={onClose}
      onCta={onChoosePlan}
    />
  );
};

const PAYWALL_PRICING_ENTRY_POINTS: Record<RecordingPaywallVariant, PricingEntryPoint> = {
  autoZoom: 'recording_paywall_auto_zoom',
  download: 'recording_paywall_download',
  recordingMinutes: 'recording_paywall_recording_minutes',
  translateMinutes: 'recording_paywall_translate_minutes',
};

type RecordingPaywallDialogProps = {
  isOpen: boolean;
  pricingEntryPoint?: null | PricingEntryPoint;
  recordingCheckoutTarget?: null | RecordingCheckoutTarget;
  variant: RecordingPaywallVariant;
  onClose: () => void;
};

const RecordingPaywallDialog = ({
  isOpen,
  pricingEntryPoint,
  recordingCheckoutTarget,
  variant,
  onClose,
}: RecordingPaywallDialogProps) => {
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const wasOpenRef = useRef(false);
  const entryPoint = pricingEntryPoint ?? PAYWALL_PRICING_ENTRY_POINTS[variant];

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      MixpanelEvents.paywallOpened({ paywall_variant: variant, pricing_entry_point: entryPoint });
    }
    wasOpenRef.current = isOpen;
  }, [entryPoint, isOpen, variant]);

  if (!isOpen) return null;

  const handleChoosePlan = () => {
    MixpanelEvents.viewPlanPaywallClick({ paywall_variant: variant, pricing_entry_point: entryPoint });
    // Every paywall overwrites the return, so one left by an abandoned purchase can't redirect a later one.
    if (recordingCheckoutTarget) {
      rememberRecordingCheckoutReturn(recordingCheckoutTarget, entryPoint);
    } else {
      clearRecordingCheckoutReturn();
    }
    onClose();
    navigate(`/${PATHS.pricing}`, {
      state: {
        path: window.location.pathname,
        pricingEntryPoint: entryPoint,
      },
    });
  };

  return (
    <PaywallDialog isMobile={isMobile} isOpen={isOpen} testId="recording-paywall-modal" onClose={onClose}>
      <RecordingPaywallModalContent variant={variant} onChoosePlan={handleChoosePlan} onClose={onClose} />
    </PaywallDialog>
  );
};

export const RecordingPaywallModal = () => {
  const isOpen = useRecordingPaywallStore((state) => state.isOpen);
  const variant = useRecordingPaywallStore((state) => state.variant);
  const pricingEntryPoint = useRecordingPaywallStore((state) => state.pricingEntryPoint);
  const recordingCheckoutTarget = useRecordingPaywallStore((state) => state.recordingCheckoutTarget);
  const onClose = useRecordingPaywallStore((state) => state.close);

  return (
    <RecordingPaywallDialog
      isOpen={isOpen}
      pricingEntryPoint={pricingEntryPoint}
      recordingCheckoutTarget={recordingCheckoutTarget}
      variant={variant}
      onClose={onClose}
    />
  );
};

export const RecordingMinutesPaywallModal = () => {
  const isOpen = useRecordingMinutesPaywallStore((state) => state.isOpen);
  const onClose = useRecordingMinutesPaywallStore((state) => state.close);

  return <RecordingPaywallDialog isOpen={isOpen} variant="recordingMinutes" onClose={onClose} />;
};
